import React from 'react';
import { ArrowLeft, Calendar, Settings } from 'lucide-react';
import { formatSignedInAs } from '../utils/formatting';

// (8 Sep 2026) Shared header for PollPage.jsx and PollResultsPage.jsx.
// Those two components had never shared a visual language with each
// other — PollPage used a solid rose-800 block, PollResultsPage a plain
// white bar — nor with the rest of the app. Per Neil's decision on 8 Sep:
// both should instead match MemberCalendarStep.jsx's own header exactly
// (blue calendar icon, title/subtitle, "Signed in as", Settings + Sign
// Out), since that gives a consistent look AND a quick exit from mid-poll.
//
// Deliberately a NEW component rather than editing MemberCalendarStep.jsx
// itself — that screen is already correct and working; this just lets the
// two poll pages match it without risking a working screen to do so.
//
// title/subtitle are freeform strings so each caller shows what's
// meaningful there — the event's own title/date on a poll page, rather
// than a calendar name. onBack is optional: MemberCalendarStep's own
// header has no back arrow (it IS the landing screen), but poll pages
// navigate FROM a calendar TO a specific poll, so they need one.
const PollHeaderBar = ({
  title,
  subtitle,
  userEmail,
  preferredName,
  firstName,
  lastName,
  isAuthenticated,
  onSignOut,
  onOpenSettings,
  onBack,
  secondaryOf, // (2 Oct 2026) primary's name when signed in as their secondary contact
}) => {
  return (
    <div className="bg-white shadow-sm border-b sticky top-0 z-10">
      {/* (21 Sep 2026) On a phone the buttons sit on their own row under the title, so the title,
          date line and "Signed in as" line get the full width instead of being squeezed into a
          narrow column beside Sign Out and Settings. From sm up it is one row, as before. */}
      <div className="max-w-2xl mx-auto px-4 py-2 sm:py-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3 min-w-0">
          {onBack && (
            <button onClick={onBack} className="text-gray-500 hover:text-gray-700 flex-shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0">
            <Calendar className="w-6 h-6 text-blue-600" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-gray-800">{title}</h1>
            {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
            {userEmail && (
              <p className="text-xs text-gray-400">Signed in as: {formatSignedInAs(preferredName, firstName, lastName, userEmail, secondaryOf)}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAuthenticated && (
            <button
              onClick={onSignOut}
              className="px-2 py-1.5 text-xs text-red-600 hover:text-red-700 font-medium border border-red-300 rounded-lg hover:bg-red-50 transition whitespace-nowrap"
            >
              Sign Out
            </button>
          )}
          <button
            onClick={onOpenSettings}
            className="flex items-center gap-1 px-2 py-1.5 text-gray-600 hover:text-gray-900 text-xs whitespace-nowrap"
          >
            <Settings className="w-3.5 h-3.5" />
            Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default PollHeaderBar;
